import { Users, ChevronRight } from "lucide-react";
import { GenerationBadge } from "./GenerationBadge";
import { CopyableId } from "@/components/ui/CopyableId";
import { EmptyState } from "@/components/ui/EmptyState";
import { formatRelative } from "@/lib/format";
import type { ReferralTree as TreeT } from "@/types/referral";

export function ReferralTree({ tree }: { tree: TreeT }) {
  const gens = Object.keys(tree.generations)
    .map(Number).sort((a, b) => a - b);

  if (gens.length === 0) {
    return <EmptyState icon={Users} title="No referrals yet"
                        description="Share your User ID with others to start building your network." />;
  }

  return (
    <div className="space-y-4">
      {gens.map((g) => {
        const list = tree.generations[String(g)];
        return (
          <div key={g} className="card-elevated overflow-hidden">
            <div className="px-4 py-3 bg-surface-50 border-b border-surface-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <GenerationBadge gen={g} />
                <span className="text-sm font-semibold text-surface-800">
                  {list.length} referral{list.length > 1 ? "s" : ""}
                </span>
              </div>
              <ChevronRight className="h-4 w-4 text-surface-400" />
            </div>
            <ul className="divide-y divide-surface-100">
              {list.map((r) => (
                <li key={r.user_id}
                    className="px-4 py-3 flex items-center justify-between hover:bg-surface-50">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-500 to-brand-700 grid place-items-center text-white text-sm font-bold">
                      {r.full_name.charAt(0)}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-surface-900">{r.full_name}</div>
                      <div className="text-xs text-surface-500 flex items-center gap-2">
                        <CopyableId value={r.user_id} />
                        <span>·</span>
                        <span>via {r.sponsor_id}</span>
                      </div>
                    </div>
                  </div>
                  <span className="text-xs text-surface-500">{formatRelative(r.joined_at)}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
